'use client';

import Image from '@/components/Image';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePinkLeopardTracking } from '../hooks/useBlackFridayTracking';
import GoodItemPc from './GoodItemPc';
import GoodItemWap from './GoodItemWap';

// 商品数据类型定义
export interface GoodListProduct {
  id?: number | string;
  name?: string;
  short_name?: string;
  shop_sku?: string;
  business_sku?: string;
  image?: {
    product?: string[];
  };
  price_info?: Array<{
    currency?: string;
    price?: string;
    sell_price?: string;
  }>;
  is_sold_out?: boolean; // 是否售罄
  [key: string]: any;
}

// 促销图片数据类型
export interface PromoImageData {
  image: string;
  wap_image?: string; // 移动端图片
  title?: string; // PC端使用
  word?: string; // 移动端使用
  description?: string; // PC端使用
  aspectRatio?: string; // 宽高比
  jump_type?: number; // 跳转类型：1-商品列表，2-商品详情
  jump_value?: string; // 跳转值
  image_alt?: string; // 图片alt
}

export interface GoodListProps {
  // 基本配置
  id?: string;
  className?: string;
  index?: number; // 商品列表索引，用于控制布局

  // 促销图片配置
  promoImage: PromoImageData;

  // 商品列表配置
  products: GoodListProduct[];

  // 设备类型
  deviceType: EPlatform;

  // 埋点相关
  tabName?: string; // 当前tab名称，用于埋点

  // 布局配置
  wapColumns?: number; // 移动端列数，默认2
  pcColumns?: {
    lg: number;
    xl?: number;
    '2xl'?: number;
  }; // PC端列数配置

  // 间距配置
  padding?: {
    wap?: string; // 移动端左右间距
    pc?: string; // PC端左右间距
  };

  // 事件回调
  onProductClick?: (product: GoodListProduct, index: number) => void;
  onProductExp?: (product: GoodListProduct, index: number) => void;
  onViewMoreClick?: () => void;

  // VIEW MORE按钮配置
  viewMoreConfig?: {
    groupId?: string;
    text?: string;
    showButton?: boolean;
  };
}

export default function GoodList({
  id,
  className = '',
  index = 0,
  promoImage,
  products = [],
  deviceType,
  tabName = 'unknown',
  wapColumns = 2,
  pcColumns = { lg: 2, xl: 2, '2xl': 2 },
  padding = {
    wap: 'px-[8px]',
    pc: 'px-[48px]',
  },
  onProductClick,
  onProductExp,
  onViewMoreClick,
  viewMoreConfig = { showButton: true, text: 'viewMore' },
}: GoodListProps) {
  const t = useTranslations('activity.blackFriday');

  // 获取埋点方法
  const { trackAdExp } = usePinkLeopardTracking();

  // 促销图片曝光状态
  const [promoImageExposed, setPromoImageExposed] = useState(false);
  const promoImageRef = useRef<HTMLDivElement>(null);

  // 是否显示全部商品
  const [showAllProducts, setShowAllProducts] = useState(false);

  // 默认最多展示6个商品
  const MAX_INITIAL_PRODUCTS = 6;

  // 计算要显示的商品列表
  const displayedProducts = showAllProducts ? products : products.slice(0, MAX_INITIAL_PRODUCTS);

  // 判断是否显示 VIEW MORE 按钮
  const shouldShowViewMoreButton =
    viewMoreConfig.showButton && products.length > MAX_INITIAL_PRODUCTS && !showAllProducts;

  const { ref } = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });

  // 促销图片曝光检测
  useEffect(() => {
    if (!promoImageRef.current || promoImageExposed) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !promoImageExposed) {
            setPromoImageExposed(true);
            // 触发促销图片曝光埋点
            trackAdExp('商品版位', tabName, index);
          }
        });
      },
      { threshold: 0.5 }, // 当促销图片50%可见时触发
    );

    observer.observe(promoImageRef.current);

    return () => {
      observer.disconnect();
    };
  }, [promoImage, promoImageExposed, trackAdExp, index]);

  // 判断设备类型
  const isWeb = deviceType === EPlatform.WEB;

  // 判断是否为奇数索引（促销图片在右侧）
  const isOddIndex = index % 2 === 1;

  // 移除了复杂的吸顶逻辑，改用简单的sticky定位

  // 获取间距类名
  const getPaddingClassName = () => {
    const wapPadding = padding.wap || 'px-[14px]';
    const pcPadding =
      padding.pc || 'md:px-[48px] lg:px-[48px] xl:px-[48px] 2xl:px-[48px] 3xl:px-[48px]';

    // 根据设备类型返回相应的类名
    if (isWeb) {
      return `${pcPadding} mx-auto`;
    } else {
      return `${wapPadding} mx-auto`;
    }
  };

  // 处理 VIEW MORE 按钮点击
  const handleViewMoreClick = (e: React.MouseEvent) => {
    e.preventDefault();

    // 展示全部商品
    setShowAllProducts(true);
    onViewMoreClick?.();
  };

  // 处理商品点击
  const handleProductClick = (product: GoodListProduct, productIndex: number) => {
    onProductClick?.(product, productIndex);
  };

  // 处理商品曝光
  const handleProductInView = (product: GoodListProduct, productIndex: number) => {
    onProductExp?.(product, productIndex);
  };

  // 生成网格类名
  const getGridClassName = () => {
    // 移动端：使用 gap-x-[4px] 确保总间距为4px，商品各占50%宽度
    const wapGrid = `grid grid-cols-${wapColumns} gap-x-[4px] gap-y-[4px]`;
    const lgCols = `lg:grid-cols-${pcColumns.lg}`;
    const xlCols = pcColumns.xl ? `xl:grid-cols-${pcColumns.xl}` : '';
    const xxlCols = pcColumns['2xl'] ? `2xl:grid-cols-${pcColumns['2xl']}` : '';
    const pcGrid = `lg:gap-x-[12px] lg:gap-y-[12px]`;

    return `${wapGrid} ${lgCols} ${xlCols} ${xxlCols} ${pcGrid}`.trim();
  };

  // 如果没有促销图片，显示占位区域
  if (!promoImage.image) {
    return (
      <div className="good-list w-full">
        <div className={`${getPaddingClassName()} py-8`}>
          <div className="w-full h-32 bg-gray-200 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">暂无促销图片</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id={id} className={`good-list w-full ${className} mb-[56px] lg:mb-[24px] relative`}>
      <div
        className={`${getPaddingClassName()}
        flex flex-col gap-0
        lg:flex-row lg:gap-[12px] ${isOddIndex ? 'lg:flex-row-reverse' : ''}
      `}
      >
        {/* 促销图片区域 - 使用简单的sticky定位 */}
        <div className="w-full lg:w-1/2 relative mb-4 lg:mb-0">
          <div
            ref={promoImageRef}
            className={`promo-image-wrap overflow-hidden ${
              isWeb ? 'rounded-[12px]' : 'rounded-[8px]'
            } w-full ${isWeb ? 'lg:sticky' : 'relative'}`}
            style={{
              // PC端设置固定高度和top值
              height: isWeb ? 'calc(100vh - 112px - 6vw)' : '179.5px',
              ...(isWeb && {
                top: 'calc(100px + 6vw)',
              }),
            }}
          >
            <div className="w-full h-full flex items-center justify-center">
              <Image
                src={isWeb ? promoImage.image : promoImage.wap_image || promoImage.image}
                alt={promoImage.image_alt}
                width={1920}
                height={1080}
                className={`block ${
                  isWeb
                    ? 'w-full h-full object-cover lg:object-cover'
                    : 'w-full h-auto object-cover'
                }`}
                priority
                sizes="100vw, (min-width: 1024px) 50vw"
              />
            </div>
          </div>
        </div>

        {/* 商品列表区域 */}
        <div className="w-full lg:w-1/2 relative">
          {products.length > 0 && (
            <div>
              {/* 商品网格 */}
              <div className={getGridClassName()}>
                {displayedProducts.map((product, productIndex) => {
                  return (
                    <div
                      key={`${product.shop_sku || product.id}-${productIndex}`}
                      className="w-full"
                    >
                      {isWeb ? (
                        <GoodItemPc
                          product={product}
                          index={productIndex}
                          onProductClick={handleProductClick}
                          onProductExp={handleProductInView}
                        />
                      ) : (
                        <GoodItemWap
                          product={product}
                          index={productIndex}
                          onProductClick={handleProductClick}
                          onProductExp={handleProductInView}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* VIEW MORE按钮 */}
              {shouldShowViewMoreButton && (
                <div className="flex justify-center mt-[16px] lg:mt-[32px]">
                  <div
                    ref={ref}
                    className={`view-more flex items-center justify-center font-bb text-white cursor-pointer transition-all duration-200 hover:opacity-90 no-underline bg-transparent ${
                      isWeb
                        ? 'w-[338px] h-[48px] rounded-[1.81vw] border border-white text-[16px]'
                        : 'w-[282px] h-[43px] rounded-[50px] border border-white text-[14px]'
                    }`}
                    style={{
                      lineHeight: isWeb ? '1.32vw' : '17px',
                    }}
                    aria-label={viewMoreConfig.text}
                    onClick={handleViewMoreClick}
                  >
                    {t('viewMore')}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
